import { Job } from '@/apis/recruitementApis'
import iconComponents from '@/assets/icons/iconComponents'
import iconUrls from '@/assets/icons/iconUrls'
import Button from '@/common/button'
import LazySpinner from '@/common/spinners/LazySpinner'
import Typography from '@/common/Typography'
import useMediaQuery from '@/hooks/useMediaQuery'
import { useAppDispatch } from '@/store/hooks'
import {
  setSelectedJob,
  useRecruiterJobsPostingQuery,
} from '@/store/recruitementSlice'
import clsx from 'clsx'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { ScaleLoader } from 'react-spinners'
import PreviewContainer from './PreviewContainer'

const JobBoard = () => {
  const { t } = useTranslation()

  const isMobile = useMediaQuery('(max-width: 600px)')
  const {
    data: topJobs,
    isLoading,
    refetch,
  } = useRecruiterJobsPostingQuery({
    page: 1,
    page_size: 10,
    status: 'Open',
    internship: false,
  })
  const navigate = useNavigate()

  const dispatchJobs = useAppDispatch()
  const selectJob = (job: any) => {
    dispatchJobs(setSelectedJob(job.id))

    if (isMobile) {
      navigate(
        `/alumni/app/recruitment/job-board/${(
          job.title || 'Product Design'
        ).replace(/\s/, '+')}?job=${job.id}&board=others`
      )
    } else {
      navigate(`/alumni/app/recruitment/job-board?job=${job.id}&board=others`)
    }
  }

  return (
    <PreviewContainer>
      <>
        <div className='flex justify-between items-center w-full py-5 px-4 gap-5 h-[56px] lg:h-[72px]'>
          <Typography className='font-semibold md md:lg text-jaa-shades-black'>
            {t('app.job_board.title')}
          </Typography>
          <Button
            onClick={() =>
              navigate('/alumni/app/recruitment/job-board?board=others')
            }
            href='/alumni/app/recruitment/job-board?board=others'
            label={`${t('app.view_all')}`}
            className='!p-0 !uppercase text-jaa-teal-100 font-semibold xs md:sm'
            variant='empty'
          />
        </div>
        {isLoading ? (
          <LazySpinner show>
            <div className='flex flex-1 w-full h-full bg-jaa-shades-white items-center justify-center'>
              <ScaleLoader color='var(--teal-100)' />
            </div>
          </LazySpinner>
        ) : topJobs?.count ? (
          <div className='flex w-full flex-col items-end flex-1 overflow-y-auto pb-4 scrollbar scrollbar-w-1 scrollbar-track-rounded-lg scrollbar-thumb-rounded-lg scrollbar-thumb-jaa-dark-teal-80 scrollbar-track-jaa-shades-white'>
            {(topJobs?.results || []).map((job, idx) => (
              <CompanyBoardSummary
                key={job.id}
                job={job}
                onClick={() => selectJob(job)}
              />
            ))}
          </div>
        ) : (
          <EmptyJobBoard onRefresh={refetch} />
        )}
      </>
    </PreviewContainer>
  )
}

export const JobTypeStyles = {
  internship: {
    backgroundColor: '#E0EFFF',
    color: '#5995D2',
  },
  full_time: {
    backgroundColor: '#FFEFE0',
    color: '#DE9D60',
  },
}
export const CompanyBoardSummary: FC<{
  job: Job
  selected?: boolean
  onClick?: () => void
}> = ({ job, selected, onClick }) => {
  return (
    <article
      onClick={onClick}
      className={clsx(
        'px-4 w-full hover:bg-[#F5F5F5] cursor-pointer',
        selected ? 'bg-[#F5F5F5]' : 'bg-jaa-shades-white'
      )}
    >
      <div className='flex w-full  gap-3 items-start py-4 border-b border-[#d9d9d9] '>
        <div className='w-9 h-9 rounded-md lg:w-[50px] lg:h-[50px] lg:rounded-lg'>
          <img
            src={job.company_logo || '/defaultImage.png'}
            alt='Representation of organization'
            className='w-full h-full object-contain object-center'
          />
        </div>
        <div className='flex flex-col items-start gap-2 flex-1 '>
          <header className='flex flex-col items-start gap-0.5'>
            <Typography
              as='h5'
              className='text-jaa-shades-black md font-medium lg:lg lg:h5'
            >
              {job.title}
            </Typography>
            <div className='flex items-center gap-2 w-full whitespace-nowrap'>
              <Typography
                as='span'
                className='font-normal sm text-jaa-shades-black'
              >
                {job.company_name}
              </Typography>
              <span className='h-4 w-[1px] bg-black'></span>
              <span className='flex items-center gap-1'>
                <img
                  src={iconUrls.recruitment.UserAltIcon}
                  alt='representation
                  of
                  people'
                  className='w-4 h-4'
                />
                <span className='text-jaa-shades-gray-2.5 xs font-normal'>
                  {job.company_size}
                </span>
              </span>
            </div>
            <div className='flex items-start gap-3'>
              <Typography className='sm font-normal text-jaa-shades-gray-2.5 flex gap-2 items-center'>
                <span className='w-0.5 h-0.5 rounded-full bg-jaa-shades-gray-2.5'></span>
                {job.job_type.toLowerCase() === 'internship' ? (
                  <span>
                    {job.paid
                      ? `Paid ( ${job.salary_range} ${
                          job.salary_currency?.split?.(' ')?.[0]
                        })`
                      : 'Unpaid'}
                  </span>
                ) : (
                  <span>
                    {job.salary_range} {job.salary_currency?.split?.(' ')?.[0]}
                  </span>
                )}
              </Typography>
              <Typography className='sm font-normal text-jaa-shades-gray-2.5 flex gap-2 items-center'>
                <span className='w-0.5 h-0.5 rounded-full bg-jaa-shades-gray-2.5'></span>
                <span
                  style={
                    JobTypeStyles[
                      job.job_type
                        .replace(/[\s-]/, '_')
                        .toLowerCase() as 'internship'
                    ] || JobTypeStyles['full_time']
                  }
                  className='py-1.5 px-3 gap-2.5 flex justify-center items-center xs'
                >
                  {job.job_type.toUpperCase()}{' '}
                  {job.job_type.toUpperCase().includes('INTERNSHIP')
                    ? 'OPPORTUNITY'
                    : ''}
                </span>
              </Typography>
            </div>
          </header>
          <Typography className='sm font-normal text-jaa-shades-black'>
            {job.description}
          </Typography>
          {!!job.images?.length && (
            <div className='flex gap-1.5 items-center bg-[#F7F7F7] h-9 py-2.5 px-2 rounded'>
              <svg
                width='20'
                height='20'
                viewBox='0 0 20 20'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M15.8333 2.5H4.16667C3.24619 2.5 2.5 3.24619 2.5 4.16667V15.8333C2.5 16.7538 3.24619 17.5 4.16667 17.5H15.8333C16.7538 17.5 17.5 16.7538 17.5 15.8333V4.16667C17.5 3.24619 16.7538 2.5 15.8333 2.5Z'
                  stroke='#979797'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M17.5001 12.5L13.3334 8.33334L4.16675 17.5'
                  stroke='#979797'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
                <path
                  d='M7.08325 8.33334C7.77361 8.33334 8.33325 7.7737 8.33325 7.08334C8.33325 6.39299 7.77361 5.83334 7.08325 5.83334C6.3929 5.83334 5.83325 6.39299 5.83325 7.08334C5.83325 7.7737 6.3929 8.33334 7.08325 8.33334Z'
                  stroke='black'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              <span className='text-sm text-[#979797]'>
                {job.images?.length}
              </span>
            </div>
          )}
        </div>
      </div>
    </article>
  )
}

export const EmptyJobBoard: FC<{
  onRefresh: () => void
  isLoading?: boolean
}> = ({ onRefresh, isLoading }) => {
  const { t } = useTranslation()

  return (
    <div className='min-h-[45vh] w-full h-full flex justify-center items-center flex-col bg-white rounded-lg self-stretch gap-6'>
      <iconComponents.recruitment.EmptyPageIcon className='w-[140px] h-[110px]' />
      <div className='flex flex-col gap-1 justify-center items-center'>
        <Typography as='h5' className='text-jaa-shades-black'>
          {t('app.job_board.empty_title')}
        </Typography>
        <Typography
          as='p'
          className='sm text-jaa-shades-gray-2.5 font-normal w-fu; text-center'
        >
          {t('app.job_board.empty_desc')}
        </Typography>
      </div>
      <Button
        onClick={onRefresh}
        loading={isLoading}
        label='Refresh'
        className='!uppercase !font-medium'
      />
    </div>
  )
}
export default JobBoard
